import { useEffect, useState, useContext } from "react"
import Header from "../../../layouts/Header"
import BackIcon from "../../../components/BackIcon"
import { useNavigate, useLocation } from "react-router-dom"
import { Box, Paper, TextField, Tooltip, IconButton, Grid, MenuItem, Typography } from "@mui/material"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import CustomBackdrop from "../../../components/CustomBackdrop"
import { useMutation, api, showWarning } from "../../../api"
import { GlobalContext } from "../../../contexts/GlobalContext"
import getIconComponent from "../../utils/getIconComponent"
import Save from "@mui/icons-material/Save"

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const StyledRoot = {
  width: "100%",
  maxWidth: "1200px",
  margin: "0 auto",
  padding: "20px",
  backgroundColor: "#f5f7fa",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
}

const EditarTipoMotivo = () => {
  const navigate = useNavigate()
  const { state } = useLocation() // Recibimos la fila seleccionada desde la grilla[cite: 20]
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Rescatamos el código original (llave) por si el usuario lo modifica[cite: 20]
  const [tmotcodigoViejo, setTmotcodigoViejo] = useState("")

  // Estado del formulario[cite: 20]
  const [formData, setFormData] = useState({
    tmotcodigo: "",
    tmotdescripcion: "",
    tmotstatus: "A",
  })

  useEffect(() => {
    // Precargar los inputs con los valores de la fila enviada desde la grilla[cite: 20]
    if (state) {
      setFormData({
        tmotcodigo: state.tmotcodigo || "",
        tmotdescripcion: state.tmotdescripcion || "",
        tmotstatus: state.tmotstatus || "A",
      })
      setTmotcodigoViejo(state.tmotcodigo || "")
    } else {
      navigate(-1) // Retorno de seguridad si entran directo a la URL[cite: 20]
    }
  }, [state, navigate])

  const {
    mutateAsync: SaveEdicionTipoMotivo,
    isPending: isSavingEdicion,
    isError,
  } = useMutation({
    queryKey: ["isEditingTipoMotivo"],
    fn: async (data) => {
      const response = await api.post("/TipoMotivo/updateTipoMotivo", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: () => navigate(-1), // Retorna a la grilla tras guardar[cite: 20]
  })

  // Manejar cambios en los campos del formulario con limpieza y topes[cite: 20]
  const handleInputChange = (field, value) => {
    let finalValue = field === "tmotstatus" ? value : value.toUpperCase()

    if (field === "tmotcodigo") finalValue = finalValue.slice(0, 3)
    else if (field === "tmotdescripcion") finalValue = finalValue.slice(0, 60)

    setFormData((prev) => ({ ...prev, [field]: finalValue }))
  }

  // Manejar envío del formulario[cite: 20]
  const handleSubmit = async (e) => {
    if (e) e.preventDefault()

    // Preparar payload tal cual lo espera el backend para la edición de la llave original[cite: 20]
    const payload = {
      tmotcodigoOld: tmotcodigoViejo,
      tmotcodigoNew: formData.tmotcodigo,
      tmotdescripcion: formData.tmotdescripcion,
      tmotstatus: formData.tmotstatus,
    }

    try {
      if (!payload.tmotcodigoNew.trim()) return showWarning("El Código del Tipo de Motivo es obligatorio")
      if (!payload.tmotdescripcion.trim()) return showWarning("La Descripción es obligatoria")

      await SaveEdicionTipoMotivo(payload)
    } catch (error) {
      console.error("Error al editar el Tipo de Motivo:", error)
      if (error?.code === "VALIDATION_ERROR") {
        showWarning(error.message)
      }
    }
  }

  // Acción dinámica de la barra de menú (TOP bar filtrada solo para GRABAR)[cite: 20]
  const actualizarAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action?.acccaption === "GRABAR")

  const toolbarActions = actualizarAction
    ? [
        {
          label: actualizarAction.acccaption,
          key: actualizarAction.acccaption,
          icon: getIconComponent(actualizarAction.accnameicono, actualizarAction.acctipoico),
        },
      ]
    : [{ label: "Actualizar", key: "ACTUALIZAR", icon: <Save /> }]

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        {/* Toolbar superior con el botón de Guardar/Actualizar[cite: 20] */}
        <Box sx={{ mb: 2 }}>
          {toolbarActions.map((action) => (
            <Tooltip title={action.label} key={action.key}>
              <IconButton
                onClick={handleSubmit}
                disabled={isSavingEdicion}
                sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, mr: 1 }}
              >
                {action.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Box>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Editar Tipo de Motivo: {tmotcodigoViejo}</b>
        </div>

        <CustomBackdrop isLoading={isSavingEdicion} />

        <Box sx={StyledRoot}>
          <Paper
            elevation={3}
            sx={{ p: 4, borderRadius: 3, background: "white" }}
            component="form"
            onSubmit={handleSubmit}
          >
            <Typography variant="h6" color="primary" gutterBottom>
              Datos del Tipo de Motivo
            </Typography>
            <Grid container spacing={3}>
              {/* CÓDIGO */}
              <Grid item xs={12} sm={3}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Código *"
                  value={formData.tmotcodigo}
                  onChange={(e) => handleInputChange("tmotcodigo", e.target.value)}
                  error={isError && !formData.tmotcodigo}
                  inputProps={{ maxLength: 3 }}
                />
              </Grid>

              {/* DESCRIPCIÓN */}
              <Grid item xs={12} sm={6}>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Descripción *"
                  value={formData.tmotdescripcion}
                  onChange={(e) => handleInputChange("tmotdescripcion", e.target.value)}
                  error={isError && !formData.tmotdescripcion}
                  inputProps={{ maxLength: 60 }}
                />
              </Grid>

              {/* ESTADO (ACTIVO/INACTIVO)[cite: 20] */}
              <Grid item xs={12} sm={3}>
                <TextField
                  select
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  label="Estado"
                  value={formData.tmotstatus}
                  onChange={(e) => handleInputChange("tmotstatus", e.target.value)}
                >
                  <MenuItem value="A">ACTIVO</MenuItem>
                  <MenuItem value="I">INACTIVO</MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </div>
    </ThemeProvider>
  )
}

export default EditarTipoMotivo
